<template>
  <div class="configuration-panel">
    <div v-if="circularDependencies.hasCycle" class="circular-dependency-warning">
      <div class="warning-icon">&#9888;</div>
      <div class="warning-content">
        <strong>Circular dependency detected</strong>
        <p>
          {{ cycleIntroText }}
          <span
            v-for="(label, idx) in conflictingFieldLabels"
            :key="idx"
            class="conflict-field"
          >{{ label }}<template v-if="idx < conflictingFieldLabels.length - 1">, </template></span>.
          {{ cycleAdviceText }}
        </p>
      </div>
    </div>

    <empty-configuration
      v-if="!rules.length"
      :label="tabLabel"
      :can-add="canManageRules"
      @add="$emit('add-rule')"
    />

    <configuration-rule-card
      v-for="rule in rules"
      :key="rule.id"
      :rule="rule"
      :tab-meta="getTabMeta(rule.feature)"
      :lists="lists"
      :selectedTab="selectedTab"
      :validation="validationState[rule.id] || {}"
      :is-editing="Boolean(rule.isEditing)"
      :is-locked="Boolean(editingRuleId) && !rule.isEditing"
      :is-in-cycle="isRuleInCycle(rule.id)"
      :can-manage-rules="canManageRules"
      @update="$emit('update-rule', $event)"
      @start-edit="editingRule($event)"
      @save-edit="$emit('save-edit-rule', $event)"
      @cancel-edit="$emit('cancel-edit-rule', $event)"
      @remove="$emit('remove-rule', $event)"
    />

    <button
      v-if="rules.length && canManageRules"
      class="add-rule"
      type="button"
      :disabled="Boolean(editingRuleId)"
      @click="$emit('add-rule')"
    >
      <span>+</span> Add another rule for {{ tabLabel }}
    </button>
  </div>
</template>

<script>
import EmptyConfiguration from './EmptyConfiguration.vue'
import ConfigurationRuleCard from './ConfigurationRuleCard.vue'
import { formatDependencyFieldKey } from './conditionHelper'

export default {
  name: 'ConfigurationPanel',
  components: {
    EmptyConfiguration,
    ConfigurationRuleCard
  },
  props: {
    rules: {
      type: Array,
      required: true
    },
    tabLabel: {
      type: String,
      required: true
    },
    tabs: {
      type: Array,
      required: true
    },
    lists: {
      type: Object,
      required: true
    },
    validationState: {
      type: Object,
      default: () => ({})
    },
    editingRuleId: {
      type: [String, Number],
      default: null
    },
    selectedTab: {
      type: Object,
      required: true
    },
    canManageRules: {
      type: Boolean,
      default: false
    },
    circularDependencies: {
      type: Object,
      default: () => ({ hasCycle: false, cycleRuleIds: [], conflictingFields: [] })
    }
  },
  computed: {
    conflictingFieldLabels () {
      return (this.circularDependencies.conflictingFields || []).map(f => formatDependencyFieldKey(f))
    },
    hasSelfReferencingRule () {
      return (this.circularDependencies.cycleEdges || [])
        .some(edge => String(edge.from) === String(edge.to))
    },
    cycleAdviceText () {
      return this.hasSelfReferencingRule
        ? "A rule cannot depend on a field it sets, and rules in a cycle may override each other's values unpredictably. Please update the conditions or actions to break the cycle before saving."
        : "Rules involved in a cycle may override each other's values unpredictably. Please update the conditions or actions to break the cycle before saving."
    },
    cycleIntroText () {
      return this.hasSelfReferencingRule
        ? 'The following fields are both read in a condition and written by an action of the same rule:'
        : 'The following fields create a cycle across rules:'
    }
  },
  methods: {
    editingRule (ruleId) {
      this.$emit('start-edit-rule', ruleId)
    },
    getTabMeta (feature) {
      return this.tabs.find(tab => tab.key === feature) || this.tabs[0]
    },
    isRuleInCycle (ruleId) {
      return (this.circularDependencies.cycleRuleIds || []).some(id => String(id) === String(ruleId))
    }
  }
}
</script>

<style lang="scss" scoped>
.configuration-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 22px 30px;
}

.add-rule {
  min-height: 44px;
  border: 1px dashed #d7dde7;
  border-radius: 8px;
  background: transparent;
  color: #98a2b3;
  font-size: var(--text-xs1);
  font-weight: 500;
  cursor: pointer;

  span {
    margin-right: 8px;
    font-size: var(--text-m);
  }

  &:hover {
    border-color: #b8c2d1;
    background: #ffffff;
    color: #667085;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.circular-dependency-warning {
  display: flex;
  gap: 12px;
  padding: 14px 18px;
  border: 1px solid #fecdca;
  border-left: 3px solid #f04438;
  border-radius: 8px;
  background: #fef3f2;
}

.warning-icon {
  flex-shrink: 0;
  color: #f04438;
  font-size: 18px;
  line-height: 1.4;
}

.warning-content strong {
  display: block;
  margin-bottom: 4px;
  color: #b42318;
  font-size: var(--text-s);
}

.warning-content p {
  margin: 0;
  color: #7a271a;
  font-size: var(--text-xs1);
  line-height: 1.5;
}

.conflict-field {
  font-weight: 600;
}
</style>
